import {useMemo, useState} from 'react'
import {Button, Drawer, Input, Menu} from 'antd'
import {LogoutOutlined, MenuOutlined} from '@ant-design/icons'
import {useKeycloak} from '@react-keycloak/web'
import {useNavigate} from 'react-router-dom'
import { useAppMessage } from '@providers'
import {RoutesFields} from '@constants'
import {useGlobalUser} from '@hooks'
import './style.scss'

const {Search} = Input

interface MenuItem {
    key: string;
    label: string;
    children?: MenuItem[];
}

export const Sidebar = () => {
    const [user] = useGlobalUser()
    const message = useAppMessage()
    const {keycloak} = useKeycloak()
    const navigate = useNavigate()
    const [visible, setVisible] = useState(false)
    const [searchInput, setSearchInput] = useState('')

    const filteredMenuItems = useMemo(() => {
        const filterItems = () => {
            return (Object.keys(RoutesFields) as (keyof typeof RoutesFields)[]).reduce<MenuItem[]>((acc, key) => {
                const route = RoutesFields[key]
                const permissions: readonly string[] = route.PERMISSIONS

                const isPublic = route.PERMISSIONS.length === 0

                const hasAccess =
                    isPublic ||
                    permissions.some((role) => user.userRoles.includes(role))

                if (route.MENU && hasAccess) {
                    if (route.GRANDPARENT) {
                        const grandParentGroup = acc.find(
                            (group) => group.key === route.GRANDPARENT
                        )
                        if (grandParentGroup) {
                            if (route.PARENT) {
                                const parentGroup = grandParentGroup.children?.find(
                                    (group) => group.key === route.PARENT
                                )
                                if (parentGroup) {
                                    parentGroup.children?.push({
                                        key: route.PATH,
                                        label: route.TITLE
                                    })
                                } else {
                                    grandParentGroup.children?.push({
                                        key: route.PARENT,
                                        label: route.PARENT,
                                        children: [
                                            {
                                                key: route.PATH,
                                                label: route.TITLE
                                            }
                                        ]
                                    })
                                }
                            } else {
                                grandParentGroup.children?.push({
                                    key: route.PATH,
                                    label: route.TITLE
                                })
                            }
                        } else {
                            acc.push({
                                key: route.GRANDPARENT,
                                label: route.GRANDPARENT,
                                children: [
                                    route.PARENT
                                        ? {
                                            key: route.PARENT,
                                            label: route.PARENT,
                                            children: [
                                                {
                                                    key: route.PATH,
                                                    label: route.TITLE
                                                }
                                            ]
                                        }
                                        : {
                                            key: route.PATH,
                                            label: route.TITLE
                                        }
                                ]
                            })
                        }
                    } else {
                        acc.push({
                            key: route.PARENT || route.PATH,
                            label: route.PARENT || route.TITLE,
                            children: route.PARENT
                                ? [
                                    {
                                        key: route.PATH,
                                        label: route.TITLE
                                    }
                                ]
                                : undefined
                        })
                    }
                }
                return acc
            }, [])
        }

        const items = filterItems()

        const flattenedItems = items
            .flatMap((item) =>
                item.children
                    ? item.children.flatMap((child) =>
                        child.children
                            ? child.children.map((subChild) => ({
                                key: subChild.key,
                                label: subChild.label
                            }))
                            : {
                                key: child.key,
                                label: child.label
                            }
                    )
                    : {
                        key: item.key,
                        label: item.label
                    }
            )
            .filter((item) =>
                item.label.toLowerCase().includes(searchInput.toLowerCase())
            )

        const filteredItems = searchInput
            ? flattenedItems
            : items.filter(
                (item) =>
                    item.label ||
                    item.children?.some((child) => (child.children?.length ?? 0) > 0)
            )

        return filteredItems
    }, [searchInput, user.userRoles])

    const handleSearchChange = (value: string) => {
        setSearchInput(value)
    }

    const showDrawer = () => {
        setVisible(true)
    }

    const onClose = () => {
        setVisible(false)
    }

    const handleLogout = () => {
        if (keycloak.authenticated) {
            keycloak
                .logout()
                .then(() => {
                    navigate('/')
                })
                .catch(() => {
                    message.error('Erro ao deslogar!!')
                    navigate('/')
                })
        }
    }

    const handleMenuClick = ({ key }: { key: string }) => {
        navigate(key)
        onClose()
    }

    return (
        <>
            <Button
                type='link'
                icon={<MenuOutlined/>}
                onClick={showDrawer}
                className='sidebar-button'
            />
            <Drawer
                title='Menu'
                placement='left'
                closable={true}
                onClose={onClose}
                open={visible}
                className='sidebar-drawer'
            >
                <div className='search-bar'>
                    <Search
                        placeholder='Buscar Páginas'
                        onSearch={handleSearchChange}
                        enterButton
                    />
                </div>

                <Menu
                    mode='inline'
                    items={filteredMenuItems}
                    onClick={handleMenuClick}
                    className='sidebar-menu'
                />
                <Button
                    type='primary'
                    icon={<LogoutOutlined/>}
                    onClick={handleLogout}
                    className='sidebar-logout-button'
                >
                    Sair
                </Button>
            </Drawer>
        </>
    )
}
